import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { AlertColor } from '@mui/material/Alert';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { makeAuthenticatedApiCall } from '../apiHelper';
import { useAuth } from '../components/AuthContext';
import { ErrorMessageSnackbar } from '../components/ErrorMessageSnackbar';
import { useAdminAgencyHomePromoMessageConfig } from '../hooks/useApiQueries';
import {
  PROMO_MESSAGE_SLOT_DEFINITIONS,
  PromoFranchiseMessage,
  PromoMessageConfig,
  PromoMessageSlot,
} from '../types/promoMessage';
import { getAllFranchises, getFranchise } from '../utils';

interface ApiResponse {
  config?: PromoMessageConfig;
}

const DEFAULT_MESSAGE_PREVIEW_KEY = '__default__';

const emptyConfig: PromoMessageConfig = {
  is_active: false,
  default_message: '',
  franchise_messages: [],
};

/**
 * Normalise les configurations enregistrées avant l'identification par
 * `franchise_id` : les plus anciennes ne connaissaient que `franchise_name`.
 */
const normalizeFranchiseMessages = (
  rawMessages: PromoFranchiseMessage[] | undefined,
  franchiseOptions: { id: string; name: string }[],
): PromoFranchiseMessage[] =>
  (rawMessages || []).map((item: PromoFranchiseMessage & { franchise_name?: string }) => {
    const resolvedId = item.franchise_id ?? franchiseOptions.find((f) => f.name === item.franchise_name)?.id ?? '';
    return { franchise_id: String(resolvedId), message: item.message };
  });

const AgencyHomePromoMessageAdminPage = () => {
  const { currentUser } = useAuth();
  const userRole = currentUser?.data?.data?.role;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeSlot, setActiveSlot] = useState<PromoMessageSlot>('home');

  // Les deux emplacements sont chargés en parallèle : l'onglet inactif sert à
  // afficher son état (actif / inactif) et à proposer la recopie de son message.
  const homeQuery = useAdminAgencyHomePromoMessageConfig('home') as { data?: ApiResponse; isLoading: boolean };
  const repriseQuery = useAdminAgencyHomePromoMessageConfig('reprise') as { data?: ApiResponse; isLoading: boolean };

  const queryBySlot: Record<PromoMessageSlot, { data?: ApiResponse; isLoading: boolean }> = {
    home: homeQuery,
    reprise: repriseQuery,
  };

  const activeQuery = queryBySlot[activeSlot];
  const otherSlot: PromoMessageSlot = activeSlot === 'home' ? 'reprise' : 'home';
  const otherSlotDefinition = PROMO_MESSAGE_SLOT_DEFINITIONS.find((definition) => definition.slot === otherSlot);
  const activeSlotDefinition = PROMO_MESSAGE_SLOT_DEFINITIONS.find((definition) => definition.slot === activeSlot);

  const [isActive, setIsActive] = useState(false);
  const [defaultMessage, setDefaultMessage] = useState('');
  const [franchiseMessages, setFranchiseMessages] = useState<PromoFranchiseMessage[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [previewTarget, setPreviewTarget] = useState<string>(DEFAULT_MESSAGE_PREVIEW_KEY);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>('success');
  const [isSaving, setIsSaving] = useState(false);

  const franchiseOptions = getAllFranchises();

  const openSnackbar = (message: string, severity: AlertColor) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // Vérifier que l'utilisateur est god
  useEffect(() => {
    if (userRole && userRole !== 'god') {
      navigate('/');
    }
  }, [userRole, navigate]);

  // Charger dans le formulaire la configuration de l'emplacement sélectionné
  useEffect(() => {
    const config = activeQuery.data?.config;
    if (!config) {
      return;
    }

    setIsActive(config.is_active);
    setDefaultMessage(config.default_message || '');
    setFranchiseMessages(normalizeFranchiseMessages(config.franchise_messages, franchiseOptions));
    setPreviewTarget(DEFAULT_MESSAGE_PREVIEW_KEY);
    setIsDirty(false);
    // franchiseOptions est reconstruit à chaque rendu : le recharger déclencherait
    // une boucle. La configuration ne dépend que de la réponse serveur et du slot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuery.data, activeSlot]);

  const handleSlotChange = (nextSlot: PromoMessageSlot) => {
    if (nextSlot === activeSlot) {
      return;
    }

    const canLeaveWithoutSaving =
      !isDirty ||
      window.confirm(
        `Des modifications ne sont pas enregistrées sur « ${activeSlotDefinition?.label} ». Changer d'emplacement les abandonnera. Continuer ?`,
      );

    if (canLeaveWithoutSaving) {
      setActiveSlot(nextSlot);
    }
  };

  const handleAddFranchise = () => {
    setFranchiseMessages([...franchiseMessages, { franchise_id: '', message: '' }]);
    setIsDirty(true);
  };

  const handleRemoveFranchise = (index: number) => {
    setFranchiseMessages(franchiseMessages.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  const handleFranchiseIdChange = (index: number, franchiseId: string) => {
    setFranchiseMessages(
      franchiseMessages.map((item, i) => (i === index ? { ...item, franchise_id: franchiseId } : item)),
    );
    setIsDirty(true);
  };

  const handleFranchiseMessageChange = (index: number, value: string) => {
    setFranchiseMessages(franchiseMessages.map((item, i) => (i === index ? { ...item, message: value } : item)));
    setIsDirty(true);
  };

  const handleCopyFromOtherSlot = () => {
    const otherConfig = queryBySlot[otherSlot].data?.config ?? emptyConfig;

    setDefaultMessage(otherConfig.default_message || '');
    setFranchiseMessages(normalizeFranchiseMessages(otherConfig.franchise_messages, franchiseOptions));
    setIsDirty(true);
    openSnackbar(
      `Contenu de « ${otherSlotDefinition?.label} » recopié. Pensez à l'adapter à la largeur de cet emplacement avant d'enregistrer.`,
      'info',
    );
  };

  const validFranchiseMessages = useMemo(
    () => franchiseMessages.filter((fm) => fm.franchise_id.trim() !== '' && fm.message.trim() !== ''),
    [franchiseMessages],
  );

  const hasNothingToDisplay = defaultMessage.trim() === '' && validFranchiseMessages.length === 0;

  const handleSave = async () => {
    try {
      setIsSaving(true);

      if (isActive && hasNothingToDisplay) {
        openSnackbar("Renseignez un message avant d'activer cet emplacement", 'error');
        setIsSaving(false);
        return;
      }

      const payload = {
        slot: activeSlot,
        is_active: isActive,
        default_message: defaultMessage.trim(),
        franchise_messages: validFranchiseMessages,
      };

      await makeAuthenticatedApiCall('post', '/api/agency-home-promo-message/admin/config', payload);

      // Invalider les caches pour forcer le rechargement
      queryClient.invalidateQueries({ queryKey: ['adminAgencyHomePromoMessageConfig'] });
      queryClient.invalidateQueries({ queryKey: ['agencyHomePromoMessageConfig'] });

      setIsDirty(false);
      openSnackbar(`« ${activeSlotDefinition?.label} » enregistré`, 'success');
    } catch (error: any) {
      console.error('Error saving config:', error);
      openSnackbar(error.response?.data?.message || 'Erreur lors de la sauvegarde', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const previewOptions = useMemo(
    () => [
      { key: DEFAULT_MESSAGE_PREVIEW_KEY, label: 'Message par défaut', message: defaultMessage },
      ...franchiseMessages
        .filter((fm) => fm.franchise_id.trim() !== '')
        .map((fm) => ({
          key: fm.franchise_id,
          label: getFranchise(fm.franchise_id),
          message: fm.message,
        })),
    ],
    [defaultMessage, franchiseMessages],
  );

  const previewedMessage = previewOptions.find((option) => option.key === previewTarget)?.message ?? defaultMessage;

  if (userRole !== 'god') {
    return null;
  }

  if (activeQuery.isLoading && !activeQuery.data) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em', color: '#1d1d1f' }}>
          Messages promo agence
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          Deux emplacements, deux messages indépendants. Chacun a son activation, son texte par défaut et ses exceptions
          par franchise.
        </Typography>
      </Box>

      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 8px 30px rgba(0,0,0,0.06)' }}>
        <Tabs
          value={activeSlot}
          onChange={(_, value: PromoMessageSlot) => handleSlotChange(value)}
          variant="fullWidth"
          sx={{
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, py: 2 },
          }}
        >
          {PROMO_MESSAGE_SLOT_DEFINITIONS.map((definition) => {
            const slotConfig = queryBySlot[definition.slot].data?.config;
            const slotIsActive = definition.slot === activeSlot ? isActive : Boolean(slotConfig?.is_active);

            return (
              <Tab
                key={definition.slot}
                value={definition.slot}
                label={
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                    <span>{definition.label}</span>
                    <Chip
                      size="small"
                      label={slotIsActive ? 'Actif' : 'Inactif'}
                      color={slotIsActive ? 'success' : 'default'}
                      variant={slotIsActive ? 'filled' : 'outlined'}
                      sx={{ height: 20, fontSize: 11 }}
                    />
                    {definition.slot === activeSlot && isDirty && (
                      <Chip size="small" label="non enregistré" color="warning" sx={{ height: 20, fontSize: 11 }} />
                    )}
                  </Stack>
                }
              />
            );
          })}
        </Tabs>

        <CardContent sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            {activeSlotDefinition?.description} Largeur utile : environ {activeSlotDefinition?.previewWidth} px.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: 'rgba(0,0,0,0.02)',
                  border: '1px solid rgba(0,0,0,0.06)',
                  borderRadius: 2,
                  px: 2,
                  py: 1.5,
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={isActive}
                      onChange={(e) => {
                        setIsActive(e.target.checked);
                        setIsDirty(true);
                      }}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                        Afficher sur « {activeSlotDefinition?.label} »
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Désactivé, aucun encart n'apparaît sur cette page. L'autre emplacement n'est pas affecté.
                      </Typography>
                    </Box>
                  }
                  className="inputElement"
                />

                <Tooltip title={`Reprendre le contenu de « ${otherSlotDefinition?.label} » dans cet emplacement`}>
                  <span>
                    <Button
                      className="secondaryDealActionButton"
                      variant="outlined"
                      startIcon={<ContentCopyIcon />}
                      onClick={handleCopyFromOtherSlot}
                      disabled={queryBySlot[otherSlot].isLoading}
                    >
                      Copier depuis « {otherSlotDefinition?.label} »
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Grid>

            {isActive && hasNothingToDisplay && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  Cet emplacement est activé mais aucun message n'est saisi : rien ne s'affichera côté agence.
                </Alert>
              </Grid>
            )}

            {/* Message par défaut */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                className="inputElement"
                multiline
                minRows={6}
                label="Message par défaut (HTML supporté)"
                value={defaultMessage}
                onChange={(e) => {
                  setDefaultMessage(e.target.value);
                  setIsDirty(true);
                }}
                helperText="Affiché pour toutes les franchises sauf celles listées ci-dessous. HTML supporté : <b>gras</b>, <br /> pour un saut de ligne, styles inline."
                InputProps={{ sx: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 } }}
              />
            </Grid>

            {/* Messages par franchise (répéteur) */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 17 }}>
                    Exceptions par franchise
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Ces franchises verront leur propre message à la place du message par défaut.
                  </Typography>
                </Box>
                <Button
                  className="secondaryDealActionButton"
                  startIcon={<AddIcon />}
                  onClick={handleAddFranchise}
                  variant="outlined"
                >
                  Ajouter
                </Button>
              </Box>

              {franchiseMessages.length === 0 && (
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', mb: 1 }}>
                  Aucune exception : toutes les franchises voient le message par défaut.
                </Typography>
              )}

              {franchiseMessages.map((franchiseMsg, index) => (
                <Card
                  key={index}
                  elevation={0}
                  sx={{ mb: 2, p: 2, backgroundColor: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.06)' }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth className="inputElement">
                        <InputLabel id={`franchise-label-${index}`}>Franchise</InputLabel>
                        <Select
                          labelId={`franchise-label-${index}`}
                          label="Franchise"
                          value={franchiseMsg.franchise_id}
                          onChange={(e) => handleFranchiseIdChange(index, e.target.value)}
                        >
                          <MenuItem value="">
                            <em>Sélectionner une franchise</em>
                          </MenuItem>
                          {franchiseOptions.map((f) => (
                            <MenuItem key={f.id} value={f.id}>
                              {f.id} - {f.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={7}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        label="Message de la franchise (HTML supporté)"
                        value={franchiseMsg.message}
                        className="inputElement"
                        onChange={(e) => handleFranchiseMessageChange(index, e.target.value)}
                        placeholder="Message HTML pour cette franchise"
                        InputProps={{
                          sx: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Tooltip title="Supprimer cette exception">
                        <IconButton onClick={() => handleRemoveFranchise(index)} color="error">
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </Card>
              ))}
            </Grid>

            {/* Actions */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', alignItems: 'center' }}>
                {isDirty && (
                  <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600 }}>
                    Modifications non enregistrées
                  </Typography>
                )}
                <Button className="secondaryDealActionButton" variant="outlined" onClick={() => navigate('/')}>
                  Fermer
                </Button>
                <Button
                  className="dealActionButton"
                  variant="contained"
                  onClick={handleSave}
                  disabled={isSaving || !isDirty}
                >
                  {isSaving ? <CircularProgress size={24} /> : `Enregistrer « ${activeSlotDefinition?.label} »`}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Prévisualisation à la largeur réelle de l'emplacement */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 8px 30px rgba(0,0,0,0.06)' }}>
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 17 }}>
                Aperçu — {activeSlotDefinition?.label}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Rendu à la largeur réelle de l'emplacement ({activeSlotDefinition?.previewWidth} px), comme le verra
                l'agence.
              </Typography>
            </Box>

            <FormControl size="small" sx={{ minWidth: 240 }} className="inputElement">
              <InputLabel id="preview-target-label">Aperçu pour</InputLabel>
              <Select
                labelId="preview-target-label"
                label="Aperçu pour"
                value={
                  previewOptions.some((option) => option.key === previewTarget)
                    ? previewTarget
                    : DEFAULT_MESSAGE_PREVIEW_KEY
                }
                onChange={(e) => setPreviewTarget(e.target.value)}
              >
                {previewOptions.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ backgroundColor: '#f5f5f7', borderRadius: 2, p: 2, overflowX: 'auto' }}>
            <Box
              sx={{
                width: activeSlotDefinition?.previewWidth,
                maxWidth: '100%',
                mx: 'auto',
                backgroundColor: '#ffffff',
                border: '1px dashed rgba(0,0,0,0.15)',
                borderRadius: 2,
                p: 2,
                minHeight: 80,
              }}
            >
              {previewedMessage && previewedMessage.trim() !== '' ? (
                <Typography variant="body2" align="center" dangerouslySetInnerHTML={{ __html: previewedMessage }} />
              ) : (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ fontStyle: 'italic' }}>
                  Rien ne s'affichera à cet emplacement
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <ErrorMessageSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        handleClose={() => setSnackbarOpen(false)}
        severity={snackbarSeverity}
      />
    </Container>
  );
};

export default AgencyHomePromoMessageAdminPage;
